import { UserRegistrationForm, UserDetail } from "../pages/investors/models/userModel";
import { useState, useCallback, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ConfirmAlert, ErrorAlert, SuccessAlert } from "../models/alertModel";
import useUserCountry from '../hooks/useUserCountry';
import useCallApiAndLoad from '../hooks/useCallApiAndLoad';
import useAuth from "./useAuth";
import { getAreaCodesEndpoint } from '../services/landlordService';
import { generatePassword } from '../helpers/randomPasswordHelper';
import { fetchResponseAdapter } from '../adapters/fetchAdapter';

export interface UserManagerServices {
  create: (form: UserRegistrationForm, token: string) => any;
  list: (contextId: string | undefined, token: string) => any;
  assign?: (contextId: string, createdId: string, token: string) => any;
  updatePassword?: (userId: string, newPass: string, token: string) => any;
  deactivate?: (userId: string, token: string) => any;
  activate?: (userId: string, token: string) => any;
}

const useUserManager = (services: UserManagerServices, contextId?: string) => {

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    resetField,
  } = useForm<UserRegistrationForm>({
    resolver: yupResolver(
      yup
        .object({
          first_name: yup.string().required('Nombre es requerido'),
          last_name: yup.string().required('Apellido es requerido'),
          email: yup
            .string()
            .email('Ingrese un formato valido para correo')
            .required('Correo electrónico es requerido'),
          phone: yup.string().required('Teléfono es requerido'),
          area_code: yup.string().required('Código de área es requerido'),
          // Password validation is intentionally disabled here because passwords are auto-generated elsewhere and not input by the user in this form.
          // password: yup.string().required('Contraseña es requerida'),
        })
        .required(),
    ),
  });

  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<SuccessAlert | ErrorAlert | ConfirmAlert>({
    success: true,
    title: '',
    text: '',
    onConfirm: () => setSweetAlert(false),
    onCancel: () => setSweetAlert(false),
  });
  const [areaCodes, setAreaCodes] = useState<any[]>([]);
  const [results, setResults] = useState<UserDetail[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { getPhoneCountryConfiguration, getCurrentCountry } = useUserCountry();
  const phone_config = getPhoneCountryConfiguration();
  const navigate = useNavigate();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const loadAreaCodes = useCallback(async () => {
    try {
      const { data } = await callEndpoint(getAreaCodesEndpoint(token));
      setAreaCodes(Array.isArray(data.data) ? data['data'] : []);
    } catch {
      setAreaCodes([]);
    }
  }, [callEndpoint, token]);
  const filteredResults = results.filter((result) =>
    result.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    result.last_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const doCreate = async (form: UserRegistrationForm) => {
    const newPassword = generatePassword();
    form.password = newPassword;
    form.username = form.email;

    const { status, data } = await callEndpoint(
      services.create(form, token),
    );

    if (status !== 201) {
      setAlertProps({
        danger: true,
        title: 'Error',
        text: 'Lo sentimos, el usuario no pudo ser creado',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
      });
      setSweetAlert(true);
      return;
    }

    // asingn user to current developer if developerId is provided
    if (contextId && services.assign) {
      const response = fetchResponseAdapter(data);
      const createdId = (
        response?.data?.tid ||
        response?.data?.id ||
        response?.data?.user_id ||
        ''
      ).toString();

      if (!createdId) {
        setAlertProps({
          danger: true,
          title: 'Error',
          text: 'Lo sentimos, no se pudo obtener el ID del usuario creado',
          onConfirm: () => setSweetAlert(false),
          onCancel: () => setSweetAlert(false),
        });
        setSweetAlert(true);
        return;
      }

      const { status: assignStatus, data: assignData } = await callEndpoint(
        services.assign!(contextId, createdId, token),
      );
      if (assignStatus !== 200 && assignStatus !== 201) {
        const assignResp = fetchResponseAdapter(assignData);
        setAlertProps({
          danger: true,
          title: 'Error',
          text: assignResp?.message || 'Lo sentimos, no fue posible asignar el usuario a la desarrolladora',
          onConfirm: () => setSweetAlert(false),
          onCancel: () => setSweetAlert(false),
        });
        setSweetAlert(true);
        return;
      }
    }

    setAlertProps({
      success: true,
      title: 'Usuario Creado',
      text: `username: ${form.email}\nContraseña: ${newPassword}`,
      copyableText: newPassword,
      onConfirm: () => {
        setSweetAlert(false);
        navigate(0);
      },
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  };


  const loadUsers = useCallback(async () => {
    try {
      const { status, data } = await callEndpoint(
        services.list(contextId, token),
      );

      if (status !== 200) {
        setResults([]);
        return;
      }

      const response = fetchResponseAdapter(data);
      const users: UserDetail[] = Array.isArray(response.data)
        ? response.data.map((user: any) => ({
          ...user,
          id: user.id ?? user.user_id ?? user.tid ?? user.developer_id,
        }))
        : [];

      setResults(users);
    } catch (error) {
      console.error(error);
      setResults([]);
    }
  }, [callEndpoint, contextId, token]);

  const generateNewPassword = async (userId: string) => {
    const newPassword = generatePassword();
    const { status, data } = await callEndpoint(
      services.updatePassword!(userId, newPassword, token),
    );

    if (status !== 200) {
      const resp = fetchResponseAdapter(data);
      setAlertProps({
        danger: true,
        title: 'Error',
        text: resp?.message || 'Lo sentimos, no fue posible actualizar la contraseña',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
      });
      setSweetAlert(true);
      return null;
    }

    setAlertProps({
      success: true,
      title: 'Nueva contraseña generada',
      text: `Contraseña: ${newPassword}`,
      copyableText: newPassword,
      onConfirm: () => setSweetAlert(false),
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);

    return newPassword;
  };

  const deactivateUser = async (userId: string) => {
    const { status, data } = await callEndpoint(
      services.deactivate!(userId, token),
    );
    if (status !== 200) {
      const resp = fetchResponseAdapter(data);
      setAlertProps({
        danger: true,
        title: 'Error',
        text: resp?.message || 'Lo sentimos, no fue posible desactivar el usuario',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
      });
      setSweetAlert(true);
      return;
    }

    setResults((prevResults) =>
      prevResults.map((user) =>
        user.id === userId ? { ...user, enabled: false } : user
      )
    );

    setAlertProps({
      success: true,
      title: 'Usuario Desactivado',
      text: 'El usuario ha sido desactivado correctamente',
      onConfirm: () => setSweetAlert(false),
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);

  };

  const activateUser = async (userId: string) => {
    const { status, data } = await callEndpoint(
      services.activate!(userId, token),
    );
    if (status !== 200) {
      const resp = fetchResponseAdapter(data);
      setAlertProps({
        danger: true,
        title: 'Error',
        text: resp?.message || 'Lo sentimos, no fue posible activar el usuario',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
      });
      setSweetAlert(true);
      return;
    }
    setResults((prevResults) =>
      prevResults.map((user) =>
        user.id === userId ? { ...user, enabled: true } : user
      )
    );
    setAlertProps({
      success: true,
      title: 'Usuario Activado',
      text: 'El usuario ha sido activado correctamente',
      onConfirm: () => setSweetAlert(false),
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  };

  const confirmChangeStatus = (userId: string, enable: boolean) => {
    setAlertProps({
      warning: true,
      title: '¿Estás seguro?',
      text: `¿Deseas ${enable ? 'activar' : 'desactivar'} este usuario?`,
      showCancel: true,
      confirmBtnBsStyle: 'success',
      confirmBtnText: 'Sí, estoy seguro',
      cancelBtnBsStyle: 'danger',
      cancelBtnText: 'Cancelar',
      onConfirm: () => {
        if (enable) {
          activateUser(userId);
        } else {
          deactivateUser(userId);
        }
      },
      onCancel: () => setSweetAlert(false),
    } as ConfirmAlert);
    setSweetAlert(true);
  };


  useEffect(() => {
    loadAreaCodes();
  }, [loadAreaCodes]);
  useEffect(() => {
    const currentCountry = getCurrentCountry();
    setValue('country', currentCountry ? currentCountry.code : '');
  }, [setValue, getCurrentCountry]);
  useEffect(() => {
    loadUsers().catch((e) => console.error(e));
  }, [loadUsers]);

  return {
    handleSubmit,
    doCreate,
    control,
    errors,
    isLoading,
    phone_config,
    areaCodes,
    resetField,
    sweetAlert,
    alertProps,
    searchQuery,
    setSearchQuery,
    filteredResults,
    generateNewPassword,
    confirmChangeStatus
  };

};

export default useUserManager;